import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Module-scope constant → builder so the rotating messages localize. Keys carry
// the explicit `common:` prefix because a module-scope builder has no enclosing
// useTranslation() for the extractor to infer the namespace from.
const buildDefaultStatusMessages = (t) => [
  t('common:loader.preparingWorkspace', 'Preparing workspace'),
  t('common:loader.computingCosts', 'Computing CBAM costs'),
  t('common:loader.loadingEmissionFactors', 'Loading emission factors'),
  t('common:loader.syncingSupplierData', 'Syncing supplier data'),
];

const CG_DEFAULT_PRIMARY_RGB = '0 184 148';

function hexToRgbTriplet(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  if (full.length !== 6) return null;
  const bigint = parseInt(full, 16);
  if (Number.isNaN(bigint)) return null;
  return `${(bigint >> 16) & 255} ${(bigint >> 8) & 255} ${bigint & 255}`;
}

const CgPageLoader = ({
  variant = 'inline',
  logo,
  brandName,
  primaryColor,
  statusMessages,
  ariaLabel,
  // Panel hosts (a card, a fixed-height diagram frame, a modal body) need a
  // shorter stage than the 320px page default. Fullscreen always wins at 100vh.
  minHeight,
}) => {
  const { t } = useTranslation('common');
  const [statusIdx, setStatusIdx] = useState(0);
  // Prefer an explicit prop, then the global brand CSS variable (set by
  // applyBranding from the .env/whitelabel config), and only fall back to the
  // hardcoded CG green as a last resort. This keeps prop-less usages
  // (e.g. DashboardLayout) on-brand instead of defaulting to Carbon Glance green.
  const primaryRgb =
    hexToRgbTriplet(primaryColor) || `var(--primary-color, ${CG_DEFAULT_PRIMARY_RGB})`;
  const messages =
    statusMessages && statusMessages.length > 0 ? statusMessages : buildDefaultStatusMessages(t);
  const loadingLabel = ariaLabel ?? t('loader.loading', 'Loading');

  useEffect(() => {
    if (messages.length <= 1) return undefined;
    const id = setInterval(() => {
      setStatusIdx((i) => (i + 1) % messages.length);
    }, 1900);
    return () => clearInterval(id);
  }, [messages.length]);

  const isFullscreen = variant === 'fullscreen';
  const showLogo = isFullscreen && Boolean(logo);

  return (
    <div
      style={{
        '--cg-page-loader-primary': primaryRgb,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: isFullscreen ? 24 : 16,
        minHeight: isFullscreen ? '100vh' : (minHeight ?? 320),
        background: isFullscreen ? '#FFFFFF' : 'transparent',
      }}
    >
      <style>{`
        @keyframes cg-page-loader-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cg-page-loader-progress {
          0% { left: -40%; }
          100% { left: 100%; }
        }
        @keyframes cg-page-loader-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.6); opacity: 0.45; }
        }
        @keyframes cg-page-loader-status-fade {
          0% { opacity: 0; transform: translateY(2px); }
          15%, 85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-2px); }
        }
        .cg-page-loader-stage { animation: cg-page-loader-fade-in 540ms ease-out both; }
        .cg-page-loader-progress-pill {
          position: absolute;
          top: 0;
          width: 40%;
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(
            90deg,
            rgb(var(--cg-page-loader-primary) / 0) 0%,
            rgb(var(--cg-page-loader-primary) / 0.85) 50%,
            rgb(var(--cg-page-loader-primary) / 0) 100%
          );
          animation: cg-page-loader-progress 1.5s cubic-bezier(0.65, 0, 0.35, 1) infinite;
        }
        .cg-page-loader-pulse-dot { animation: cg-page-loader-pulse 1.4s ease-in-out infinite; }
        .cg-page-loader-status-text { animation: cg-page-loader-status-fade 1.9s ease-in-out both; }
        @media (prefers-reduced-motion: reduce) {
          .cg-page-loader-progress-pill,
          .cg-page-loader-pulse-dot,
          .cg-page-loader-status-text,
          .cg-page-loader-stage { animation: none !important; }
          .cg-page-loader-progress-pill { left: 30%; opacity: 0.6; }
        }
      `}</style>

      <div
        className="cg-page-loader-stage"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isFullscreen ? 36 : 24,
          width: '100%',
          maxWidth: 320,
        }}
      >
        {showLogo && (
          <img
            src={logo}
            alt={brandName ? `${brandName} logo` : 'logo'}
            style={{ maxWidth: 220, width: '100%', height: 'auto' }}
          />
        )}

        <div
          role="status"
          aria-live="polite"
          aria-label={loadingLabel}
          style={{
            position: 'relative',
            width: '100%',
            height: 3,
            borderRadius: 999,
            background: '#F1F2F4',
            overflow: 'hidden',
          }}
        >
          <div className="cg-page-loader-progress-pill" />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: '#6B7280',
            fontWeight: 500,
            minHeight: 18,
          }}
        >
          <span
            className="cg-page-loader-pulse-dot"
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'rgb(var(--cg-page-loader-primary))',
              flexShrink: 0,
            }}
          />
          <span
            key={statusIdx}
            className="cg-page-loader-status-text"
            style={{ display: 'inline-block' }}
          >
            {messages[statusIdx]}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CgPageLoader;
